/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, X, Edit2, ToggleLeft, ToggleRight, Hammer, AlertCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { strumentiAPI } from '../api/strumenti';

const ACCENT = '#8B5CF6';

const STATI_PEZZO = {
  disponibile:     { label: 'Disponibile',     bg: '#D1FAE5', color: '#065F46' },
  in_uso:          { label: 'In uso',          bg: '#DBEAFE', color: '#1E40AF' },
  in_manutenzione: { label: 'In manutenzione', bg: '#FEF3C7', color: '#92400E' },
  rotto:           { label: 'Rotto',           bg: '#FEE2E2', color: '#991B1B' },
  dismesso:        { label: 'Dismesso',        bg: '#E5E7EB', color: '#4B5563' },
};

export default function Strumenti() {
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const queryClient = useQueryClient();

  const { data: strumenti = [], isLoading } = useQuery({
    queryKey: ['strumenti', filtroCategoria, search],
    queryFn: () => strumentiAPI.list({
      categoria: filtroCategoria || undefined,
      q: search || undefined,
    }),
  });

  const { data: categorie = [] } = useQuery({
    queryKey: ['strumenti-categorie'],
    queryFn: () => strumentiAPI.categorie(),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, attivo }) => strumentiAPI.toggleAttivo(id, attivo),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['strumenti'] }),
  });

  return (
    <AdminLayout title="Strumenti">
      {/* Toolbar */}
      <div style={styles.toolbar}>
        <div style={styles.filters}>
          <button
            onClick={() => setFiltroCategoria('')}
            style={{
              ...styles.filterBtn,
              ...(filtroCategoria === '' ? styles.filterBtnActive : {}),
            }}
          >
            Tutte
          </button>
          {categorie.map((c) => (
            <button
              key={c}
              onClick={() => setFiltroCategoria(c)}
              style={{
                ...styles.filterBtn,
                ...(filtroCategoria === c ? styles.filterBtnActive : {}),
              }}
            >
              {c}
            </button>
          ))}
        </div>
        <button onClick={() => setCreating(true)} style={styles.addBtn}>
          <Plus size={16} /> Nuovo strumento
        </button>
      </div>

      {/* Search */}
      <div style={styles.searchWrapper}>
        <Search size={16} style={styles.searchIcon} />
        <input
          type="text"
          placeholder="Cerca per nome, codice o marca..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {/* Tabella */}
      <div style={styles.tableWrapper}>
        {isLoading && <div style={styles.empty}>Caricamento...</div>}
        {!isLoading && strumenti.length === 0 && (
          <div style={styles.empty}>
            <Hammer size={40} style={{ marginBottom: '0.5rem', color: '#9CA3AF' }} />
            <p>Nessuno strumento trovato.</p>
          </div>
        )}
        {!isLoading && strumenti.length > 0 && (
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={styles.th}>Codice</th>
                <th style={styles.th}>Nome</th>
                <th style={styles.th}>Categoria</th>
                <th style={styles.th}>Marca / Modello</th>
                <th style={styles.th}>Gestione</th>
                <th style={styles.th}>Disponibilità</th>
                <th style={styles.th}>Noleggio</th>
                <th style={styles.th}>Stato</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {strumenti.map((s) => (
                <tr
                  key={s.id}
                  style={styles.tr}
                  onClick={() => setDetailId(s.id)}
                >
                  <td style={styles.td}><code style={styles.code}>{s.codice || '—'}</code></td>
                  <td style={styles.td}><strong>{s.nome}</strong></td>
                  <td style={styles.td}>{s.categoria || '—'}</td>
                  <td style={styles.td}>
                    {s.marca || ''} {s.modello ? `· ${s.modello}` : ''}
                    {!s.marca && !s.modello ? '—' : ''}
                  </td>
                  <td style={styles.td}>
                    {s.tracked_per_pezzo ? (
                      <span style={styles.trackedBadge}>Per pezzo</span>
                    ) : (
                      <span style={styles.untrackedBadge}>Per quantità</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    {s.tracked_per_pezzo
                      ? `${s.pezzi_disponibili} / ${s.pezzi_totali}`
                      : `${parseFloat(s.quantita_magazzino)} pz`
                    }
                    {!s.tracked_per_pezzo && parseFloat(s.quantita_magazzino) <= parseFloat(s.quantita_minima) && parseFloat(s.quantita_minima) > 0 && (
                      <AlertCircle size={14} style={{ marginLeft: '0.3rem', color: '#DC2626', verticalAlign: 'middle' }} title="Scorta sotto la soglia minima" />
                    )}
                  </td>
                  <td style={styles.td}>
                    {s.costo_noleggio_default
                      ? <span style={{ color: '#059669', fontWeight: 600 }}>€ {parseFloat(s.costo_noleggio_default).toFixed(2)}</span>
                      : <span style={{ color: '#9CA3AF' }}>—</span>
                    }
                  </td>
                  <td style={styles.td}>
                    {s.attivo
                      ? <span style={{ ...styles.statusBadge, backgroundColor: '#D1FAE5', color: '#065F46' }}>Attivo</span>
                      : <span style={{ ...styles.statusBadge, backgroundColor: '#FEE2E2', color: '#991B1B' }}>Disattivato</span>
                    }
                  </td>
                  <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button onClick={() => setEditing(s)} style={styles.iconBtn} title="Modifica">
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => toggleMutation.mutate({ id: s.id, attivo: !s.attivo })}
                        style={styles.iconBtn}
                        title={s.attivo ? 'Disattiva' : 'Attiva'}
                      >
                        {s.attivo ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(creating || editing) && (
        <StrumentoFormModal
          initial={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            queryClient.invalidateQueries({ queryKey: ['strumenti'] });
            queryClient.invalidateQueries({ queryKey: ['strumenti-categorie'] });
          }}
        />
      )}

      {detailId && (
        <StrumentoDetailModal
          id={detailId}
          onClose={() => setDetailId(null)}
        />
      )}
    </AdminLayout>
  );
}

// ============================================================
// Modal: crea / modifica tipo strumento
// ============================================================
function StrumentoFormModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    codice: initial?.codice || '',
    nome: initial?.nome || '',
    descrizione: initial?.descrizione || '',
    categoria: initial?.categoria || '',
    marca: initial?.marca || '',
    modello: initial?.modello || '',
    tracked_per_pezzo: initial?.tracked_per_pezzo || false,
    quantita_magazzino: initial?.quantita_magazzino || 0,
    quantita_minima: initial?.quantita_minima || 0,
    costo_noleggio_default: initial?.costo_noleggio_default || '',
    note: initial?.note || '',
  });
  const [error, setError] = useState('');

  const saveMutation = useMutation({
    mutationFn: (data) => initial
      ? strumentiAPI.update(initial.id, data)
      : strumentiAPI.create(data),
    onSuccess: onSaved,
    onError: (err) => setError(err.response?.data?.error || 'Errore nel salvataggio'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      ...form,
      costo_noleggio_default: form.costo_noleggio_default === '' ? null : parseFloat(form.costo_noleggio_default),
      quantita_magazzino: parseFloat(form.quantita_magazzino) || 0,
      quantita_minima: parseFloat(form.quantita_minima) || 0,
    };
    saveMutation.mutate(payload);
  };

  const handle = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [field]: value });
    setError('');
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={{ margin: 0 }}>{initial ? 'Modifica strumento' : 'Nuovo strumento'}</h2>
          <button onClick={onClose} style={styles.closeBtn}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.row}>
            <div style={{ ...styles.field, flex: 1 }}>
              <label style={styles.label}>Codice</label>
              <input value={form.codice} onChange={handle('codice')} style={styles.input} placeholder="es. SALD-001" />
            </div>
            <div style={{ ...styles.field, flex: 2 }}>
              <label style={styles.label}>Nome *</label>
              <input value={form.nome} onChange={handle('nome')} style={styles.input} required />
            </div>
          </div>

          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Categoria</label>
              <input value={form.categoria} onChange={handle('categoria')} style={styles.input} placeholder="es. Saldatura" />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Marca</label>
              <input value={form.marca} onChange={handle('marca')} style={styles.input} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Modello</label>
              <input value={form.modello} onChange={handle('modello')} style={styles.input} />
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Descrizione</label>
            <textarea value={form.descrizione} onChange={handle('descrizione')} style={{ ...styles.input, minHeight: '60px' }} />
          </div>

          <div style={styles.checkboxRow}>
            <input
              type="checkbox"
              id="tracked"
              checked={form.tracked_per_pezzo}
              onChange={handle('tracked_per_pezzo')}
              style={{ width: '16px', height: '16px' }}
            />
            <label htmlFor="tracked" style={styles.checkboxLabel}>
              <strong>Tracciato per pezzo singolo</strong>
              <small style={styles.hint}>Per strumenti grandi o costosi che richiedono un seriale (es. saldatrici, trapani). Lascia spento per utensili comuni gestiti per quantità.</small>
            </label>
          </div>

          {!form.tracked_per_pezzo && (
            <div style={styles.row}>
              <div style={styles.field}>
                <label style={styles.label}>Quantità magazzino</label>
                <input type="number" step="0.01" min="0" value={form.quantita_magazzino} onChange={handle('quantita_magazzino')} style={styles.input} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Quantità minima</label>
                <input type="number" step="0.01" min="0" value={form.quantita_minima} onChange={handle('quantita_minima')} style={styles.input} />
                <small style={styles.hint}>Sotto questa soglia: alert scorta bassa.</small>
              </div>
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>Costo noleggio default (€)</label>
            <input type="number" step="0.01" min="0" value={form.costo_noleggio_default} onChange={handle('costo_noleggio_default')} style={styles.input} placeholder="Vuoto = non addebita" />
            <small style={styles.hint}>Importo proposto in automatico negli interventi. Editabile per ogni utilizzo.</small>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Note interne</label>
            <textarea value={form.note} onChange={handle('note')} style={{ ...styles.input, minHeight: '50px' }} />
          </div>

          {error && <div style={styles.errorBox}>{error}</div>}

          <div style={styles.modalActions}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>Annulla</button>
            <button type="submit" disabled={saveMutation.isPending} style={styles.submitBtn}>
              {saveMutation.isPending ? 'Salvataggio...' : (initial ? 'Salva modifiche' : 'Crea strumento')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// Modal: dettaglio strumento + gestione pezzi
// ============================================================
function StrumentoDetailModal({ id, onClose }) {
  const queryClient = useQueryClient();
  const [addingPezzo, setAddingPezzo] = useState(false);

  const { data: strumento, isLoading } = useQuery({
    queryKey: ['strumento', id],
    queryFn: () => strumentiAPI.detail(id),
  });

  const changeStatoMutation = useMutation({
    mutationFn: ({ pezzoId, stato }) => strumentiAPI.changeStatoPezzo(pezzoId, stato),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strumento', id] });
      queryClient.invalidateQueries({ queryKey: ['strumenti'] });
    },
  });

  const deletePezzoMutation = useMutation({
    mutationFn: (pezzoId) => strumentiAPI.deletePezzo(pezzoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['strumento', id] });
      queryClient.invalidateQueries({ queryKey: ['strumenti'] });
    },
  });

  if (isLoading || !strumento) {
    return (
      <div style={styles.modalOverlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={{ padding: '2rem', textAlign: 'center' }}>Caricamento...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: '720px' }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={{ margin: 0 }}>{strumento.nome}</h2>
            {strumento.codice && <code style={{ ...styles.code, marginTop: '0.3rem', display: 'inline-block' }}>{strumento.codice}</code>}
          </div>
          <button onClick={onClose} style={styles.closeBtn}><X size={20} /></button>
        </div>

        <div style={{ padding: '1.25rem 1.5rem' }}>
          <div style={styles.infoGrid}>
            {strumento.categoria && <div><strong>Categoria:</strong> {strumento.categoria}</div>}
            {strumento.marca && <div><strong>Marca:</strong> {strumento.marca}</div>}
            {strumento.modello && <div><strong>Modello:</strong> {strumento.modello}</div>}
            {strumento.costo_noleggio_default && <div><strong>Noleggio:</strong> € {parseFloat(strumento.costo_noleggio_default).toFixed(2)}</div>}
          </div>

          {strumento.descrizione && <p style={{ color: '#6B7280', marginTop: '1rem' }}>{strumento.descrizione}</p>}

          <hr style={styles.hr} />

          {strumento.tracked_per_pezzo ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0 }}>Pezzi tracciati ({strumento.pezzi.length})</h3>
                <button onClick={() => setAddingPezzo(true)} style={styles.addBtnSmall}>
                  <Plus size={14} /> Aggiungi pezzo
                </button>
              </div>

              {strumento.pezzi.length === 0 ? (
                <p style={{ color: '#6B7280' }}>Nessun pezzo registrato. Aggiungi il primo.</p>
              ) : (
                <div style={styles.pezziList}>
                  {strumento.pezzi.map((p) => {
                    const stato = STATI_PEZZO[p.stato] || STATI_PEZZO.disponibile;
                    return (
                      <div key={p.id} style={styles.pezzoCard}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>{p.numero_seriale || '(senza seriale)'}</div>
                          <div style={{ fontSize: '0.82rem', color: '#6B7280', marginTop: '0.2rem' }}>
                            {p.ubicazione && <>📍 {p.ubicazione} · </>}
                            {p.assegnato_nome && <>👤 {p.assegnato_nome} {p.assegnato_cognome}</>}
                            {!p.ubicazione && !p.assegnato_nome && 'Magazzino'}
                          </div>
                        </div>
                        <select
                          value={p.stato}
                          onChange={(e) => changeStatoMutation.mutate({ pezzoId: p.id, stato: e.target.value })}
                          style={{ ...styles.statoSelect, backgroundColor: stato.bg, color: stato.color }}
                        >
                          {Object.entries(STATI_PEZZO).map(([k, v]) => (
                            <option key={k} value={k}>{v.label}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => { if (window.confirm('Eliminare questo pezzo?')) deletePezzoMutation.mutate(p.id); }}
                          style={{ ...styles.iconBtn, color: '#DC2626' }}
                          title="Elimina pezzo"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {addingPezzo && (
                <AggiungiPezzoForm
                  tipoId={id}
                  onCancel={() => setAddingPezzo(false)}
                  onSaved={() => {
                    setAddingPezzo(false);
                    queryClient.invalidateQueries({ queryKey: ['strumento', id] });
                    queryClient.invalidateQueries({ queryKey: ['strumenti'] });
                  }}
                />
              )}
            </>
          ) : (
            <div style={styles.untrackedBox}>
              <p style={{ margin: 0 }}>Strumento gestito <strong>per quantità</strong>.</p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '1.5rem', fontWeight: 700 }}>
                {parseFloat(strumento.quantita_magazzino)} pz in magazzino
              </p>
              {parseFloat(strumento.quantita_minima) > 0 && (
                <p style={{ margin: '0.3rem 0 0', fontSize: '0.85rem', color: '#6B7280' }}>
                  Soglia minima: {parseFloat(strumento.quantita_minima)} pz
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Inline form: aggiungi pezzo
// ============================================================
function AggiungiPezzoForm({ tipoId, onCancel, onSaved }) {
  const [form, setForm] = useState({
    numero_seriale: '',
    data_acquisto: '',
    valore_acquisto: '',
    ubicazione: '',
    note: '',
  });
  const [error, setError] = useState('');

  const addMutation = useMutation({
    mutationFn: (data) => strumentiAPI.addPezzo(tipoId, data),
    onSuccess: onSaved,
    onError: (err) => setError(err.response?.data?.error || 'Errore'),
  });

  const handle = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    setError('');
  };

  const submit = (e) => {
    e.preventDefault();
    setError('');
    addMutation.mutate({
      ...form,
      valore_acquisto: form.valore_acquisto === '' ? null : parseFloat(form.valore_acquisto),
      data_acquisto: form.data_acquisto || null,
    });
  };

  return (
    <form onSubmit={submit} style={styles.addPezzoForm}>
      <h4 style={{ margin: '0 0 0.75rem' }}>Nuovo pezzo</h4>
      <div style={styles.row}>
        <div style={styles.field}>
          <label style={styles.label}>Numero seriale</label>
          <input value={form.numero_seriale} onChange={handle('numero_seriale')} style={styles.input} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Ubicazione</label>
          <input value={form.ubicazione} onChange={handle('ubicazione')} style={styles.input} placeholder="es. magazzino, furgone..." />
        </div>
      </div>
      <div style={styles.row}>
        <div style={styles.field}>
          <label style={styles.label}>Data acquisto</label>
          <input type="date" value={form.data_acquisto} onChange={handle('data_acquisto')} style={styles.input} />
        </div>
        <div style={styles.field}>
          <label style={styles.label}>Valore acquisto (€)</label>
          <input type="number" step="0.01" min="0" value={form.valore_acquisto} onChange={handle('valore_acquisto')} style={styles.input} />
        </div>
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}
      <div style={styles.modalActions}>
        <button type="button" onClick={onCancel} style={styles.cancelBtn}>Annulla</button>
        <button type="submit" disabled={addMutation.isPending} style={styles.submitBtn}>
          {addMutation.isPending ? 'Aggiunta...' : 'Aggiungi pezzo'}
        </button>
      </div>
    </form>
  );
}

const styles = {
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' },
  filters: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  filterBtn: { padding: '0.45rem 0.9rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 500 },
  filterBtnActive: { backgroundColor: ACCENT, color: 'white', borderColor: ACCENT },
  addBtn: { display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
  addBtnSmall: { display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.7rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 },
  searchWrapper: { position: 'relative', marginBottom: '1rem' },
  searchIcon: { position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' },
  searchInput: { width: '100%', padding: '0.55rem 0.85rem 0.55rem 2.3rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.92rem', outline: 'none' },
  tableWrapper: { backgroundColor: 'white', borderRadius: '12px', overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '720px' },
  thead: { backgroundColor: '#F9FAFB' },
  th: { padding: '0.85rem 1rem', textAlign: 'left', fontSize: '0.78rem', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' },
  tr: { borderTop: '1px solid #F3F4F6', cursor: 'pointer' },
  td: { padding: '0.85rem 1rem', fontSize: '0.92rem', color: '#374151' },
  code: { backgroundColor: '#F3F4F6', padding: '0.15rem 0.45rem', borderRadius: '4px', fontSize: '0.82rem', fontFamily: 'ui-monospace, monospace' },
  trackedBadge: { padding: '0.18rem 0.55rem', backgroundColor: '#EDE9FE', color: '#5B21B6', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600 },
  untrackedBadge: { padding: '0.18rem 0.55rem', backgroundColor: '#E0F2FE', color: '#075985', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600 },
  statusBadge: { padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600 },
  iconBtn: { padding: '0.35rem', backgroundColor: 'transparent', border: '1px solid #E5E7EB', borderRadius: '6px', cursor: 'pointer', color: '#374151', display: 'inline-flex' },
  empty: { padding: '3rem', textAlign: 'center', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center' },

  modalOverlay: { position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' },
  modal: { backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflow: 'auto' },
  modalHeader: { padding: '1.25rem 1.5rem', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' },
  form: { padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem', flex: 1, minWidth: '150px' },
  row: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  label: { fontSize: '0.82rem', fontWeight: 600, color: '#374151' },
  input: { padding: '0.55rem 0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', width: '100%', fontFamily: 'inherit' },
  hint: { fontSize: '0.75rem', color: '#6B7280' },
  checkboxRow: { display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.75rem', backgroundColor: '#F9FAFB', borderRadius: '8px' },
  checkboxLabel: { display: 'flex', flexDirection: 'column', gap: '0.25rem', cursor: 'pointer', fontSize: '0.88rem' },
  errorBox: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.65rem', borderRadius: '8px', fontSize: '0.85rem' },
  modalActions: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' },
  cancelBtn: { padding: '0.55rem 1.1rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 },
  submitBtn: { padding: '0.55rem 1.1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },

  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', fontSize: '0.92rem' },
  hr: { border: 'none', borderTop: '1px solid #E5E7EB', margin: '1.25rem 0' },
  pezziList: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  pezzoCard: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', backgroundColor: '#F9FAFB', borderRadius: '8px' },
  statoSelect: { padding: '0.35rem 0.6rem', border: 'none', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' },
  untrackedBox: { padding: '1.5rem', backgroundColor: '#F9FAFB', borderRadius: '12px', textAlign: 'center' },
  addPezzoForm: { marginTop: '1rem', padding: '1rem', backgroundColor: '#F9FAFB', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '0.75rem' },
};
